// src/hooks/programs/useProgramForm.js

import { useState, useEffect, useRef, useCallback } from "react";
import toast from "react-hot-toast";
import { INITIAL_FORM_DATA } from "../../components/programs/ProgramForm/helpers/formConstants";
import { initializeFormData } from "../../components/programs/ProgramForm/helpers/formInitializer";
import { validateStep as validateFormStep } from "../../components/programs/ProgramForm/helpers/formValidator";
import { buildPayload as buildFormPayload } from "../../components/programs/ProgramForm/helpers/payloadBuilder";
import useProgramDraft from "./useProgramDraft";

/**
 * Custom hook for program form management with autosave & draft restoration
 */
const useProgramForm = (isEdit, currentData, isArabic) => {
    const programId = currentData?.id || null;

    // Draft management hook
    const {
        hasDraft,
        draftTimestamp,
        draftStep,
        lastSavedAt,
        saveDraft,
        saveDraftNow,
        loadDraft,
        clearDraft,
    } = useProgramDraft({ isEdit, programId });

    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState(INITIAL_FORM_DATA);
    const [errors, setErrors] = useState({});
    const [isRestoredFromDraft, setIsRestoredFromDraft] = useState(false);

    // Track whether initial hydration has completed so we don't save initial state over a draft
    const isReadyToAutoSaveRef = useRef(false);

    // Initial hydration: check for draft or initialize standard data
    useEffect(() => {
        const savedDraft = loadDraft();

        if (savedDraft && savedDraft.formData) {
            // Restore from draft
            setFormData(savedDraft.formData);
            if (typeof savedDraft.currentStep === "number") {
                setCurrentStep(savedDraft.currentStep);
            }
            setIsRestoredFromDraft(true);
        } else if (isEdit && currentData) {
            // Edit mode without draft: initialize from currentData
            const initializedData = initializeFormData(currentData);
            setFormData(initializedData);
            setCurrentStep(0);
            setIsRestoredFromDraft(false);
        } else {
            // Create mode without draft: standard clean form
            setFormData(INITIAL_FORM_DATA);
            setCurrentStep(0);
            setIsRestoredFromDraft(false);
        }

        // Allow autosave to start recording after initial render
        const timer = setTimeout(() => {
            isReadyToAutoSaveRef.current = true;
        }, 300);

        return () => clearTimeout(timer);
    }, [isEdit, programId, currentData, loadDraft]);

    // Autosave whenever formData changes (debounced)
    useEffect(() => {
        if (!isReadyToAutoSaveRef.current) return;
        saveDraft(formData, currentStep);
    }, [formData, currentStep, saveDraft]);

    // Discard draft and start fresh
    const discardDraft = useCallback(() => {
        clearDraft();
        setIsRestoredFromDraft(false);
        setErrors({});
        setCurrentStep(0);

        if (isEdit && currentData) {
            setFormData(initializeFormData(currentData));
        } else {
            setFormData(INITIAL_FORM_DATA);
        }

        toast.success(
            isArabic
                ? "تم حذف المسودة والبدء من جديد"
                : "Draft discarded, started fresh"
        );
    }, [clearDraft, isEdit, currentData, isArabic]);

    // ✅ Normalize itinerary: clamp per_night product nights to durations_nights
    const normalizeItinerary = () => {
        const maxNights = formData.durations_nights || 0;
        let changed = false;

        const normalizedItinerary = formData.itinerary?.map(day => {
            const normalizedProducts = day.products?.map(product => {
                const pricingUnit = product.product_snapshot?.pricing_unit;
                if (
                    pricingUnit === 'per_night' &&
                    product.dynamic_fields?.nights != null &&
                    product.dynamic_fields.nights > maxNights
                ) {
                    changed = true;
                    return {
                        ...product,
                        dynamic_fields: {
                            ...product.dynamic_fields,
                            nights: maxNights,
                        },
                    };
                }
                return product;
            }) || [];

            return changed ? { ...day, products: normalizedProducts } : day;
        }) || [];

        if (changed) {
            const updatedFormData = { ...formData, itinerary: normalizedItinerary };
            setFormData(updatedFormData);
            return updatedFormData;
        }
        return formData;
    };

    // Validate current step. Accept optional activeLang to handle multilingual fields.
    const validateStep = (step, dataToValidate, activeLang = null) => {
        const newErrors = validateFormStep(step, dataToValidate, isEdit, isArabic, activeLang);
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle next step
    const handleNext = (activeLang = null) => {
        // ✅ Normalize itinerary before validation to fix stale per_night nights
        const dataToValidate = normalizeItinerary();

        if (!validateStep(currentStep, dataToValidate, activeLang)) {
            toast.error(
                isArabic
                    ? "الرجاء تعبئة جميع الحقول المطلوبة بشكل صحيح"
                    : "Please fill all required fields correctly"
            );
            return false;
        }

        const nextStep = currentStep + 1;
        setCurrentStep(nextStep);
        setErrors({});

        // Save immediately upon advancing steps
        saveDraftNow(dataToValidate, nextStep);
        return true;
    };

    // Handle previous step
    const handleBack = () => {
        if (currentStep > 0) {
            const prevStep = currentStep - 1;
            setCurrentStep(prevStep);
            setErrors({});

            // Save immediately upon moving back
            saveDraftNow(formData, prevStep);
        }
    };

    // Build payload for submission
    const buildPayload = () => {
        return buildFormPayload(formData);
    };

    return {
        currentStep,
        setCurrentStep,
        formData,
        setFormData,
        errors,
        setErrors,
        validateStep,
        handleNext,
        handleBack,
        buildPayload,
        // Draft management exports
        hasDraft,
        draftTimestamp,
        draftStep,
        lastSavedAt,
        isRestoredFromDraft,
        clearDraft,
        discardDraft,
    };
};

export default useProgramForm;
